import React, { memo } from "react";
import type { Track } from "../../../types";
import { Type, Columns, Camera, GripVertical, Magnet, X, VolumeX, Volume2, Eye, EyeOff } from "lucide-react";

export const TrackHeader = memo(function TrackHeader({
    track,
    isBeingDragged,
    onToggleMagnetic,
    onToggleVisibility,
    onToggleMute,
    onTrackVolumeChange,
    onDeleteTrack,
    onDragStart
}: {
    track: Track;
    isBeingDragged: boolean;
    onToggleMagnetic: () => void;
    onToggleVisibility: () => void;
    onToggleMute: () => void;
    onTrackVolumeChange: (volume: number) => void;
    onDeleteTrack: () => void;
    onDragStart: (e: React.MouseEvent) => void;
}) {
    const isFixed = ['t1', 's1', 'z1'].includes(track.id);
    let icon = null;
    if (track.id === 't1') icon = <Type className="w-4 h-4 text-gray-400" />;
    if (track.id === 's1') icon = <Columns className="w-4 h-4 text-gray-400" />;
    if (track.id === 'z1') icon = <Camera className="w-4 h-4 text-gray-400" />;

    return (
        <div className={`relative h-14 mb-2 flex items-center justify-between px-2 border-b border-gray-900 ${isBeingDragged ? 'opacity-50 bg-gray-800' : ''}`}>
            <div className="flex items-center gap-2 flex-1 min-w-0">
                {isFixed ? <div className="w-4 h-4 shrink-0" /> : <button onMouseDown={onDragStart} className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-300"><GripVertical className="w-4 h-4" /></button>}
                {icon && <div className="shrink-0">{icon}</div>}
                <span className="font-bold text-sm text-gray-300 truncate">{track.label}</span>
            </div>
            <div className="flex items-center gap-0.5">
                {!isFixed && (
                    <>
                        <button onClick={onToggleMagnetic} className={`p-1 rounded-md ${track.isMagnetic ? 'text-blue-400' : 'text-gray-500'} hover:bg-gray-800`} title={track.isMagnetic ? 'Disable Snapping' : 'Enable Snapping'}><Magnet className="w-4 h-4" /></button>
                        <button onClick={onDeleteTrack} className="p-1 rounded-md text-gray-500 hover:bg-gray-800 hover:text-red-400" title="Delete Track"><X className="w-4 h-4" /></button>
                        <div className="w-px h-5 bg-gray-800 mx-1"></div>
                    </>
                )}
                {track.accepts.includes('audio') && !isFixed && (
                    <div className="flex items-center gap-1.5 pr-1">
                        <button onClick={onToggleMute} className="p-1 rounded-md text-gray-400 hover:bg-gray-800" title={track.isMuted ? 'Unmute' : 'Mute'}>
                            {track.isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                        </button>
                        <input
                            type="range"
                            min={0}
                            max={100}
                            value={Math.round((track.volume ?? 1) * 100)}
                            onChange={(e) => onTrackVolumeChange(Math.max(0, Math.min(1, Number(e.target.value) / 100)))}
                            className="w-14 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2 [&::-webkit-slider-thumb]:h-2 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-400"
                            title={`Track volume ${Math.round((track.volume ?? 1) * 100)}%`}
                        />
                    </div>
                )}
                <button onClick={onToggleVisibility} className="p-1 rounded-md text-gray-400 hover:bg-gray-800" title={track.isVisible ? 'Hide' : 'Show'}>
                    {track.isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
            </div>
        </div>
    );
});
